import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { projectsAPI, categoriesAPI } from '../utils/api';
import ProjectCard from '../components/ProjectCard';

const Work = () => {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [projectsRes, categoriesRes] = await Promise.all([
        projectsAPI.getAll(),
        categoriesAPI.getAll()
      ]);
      setProjects(projectsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const response = await projectsAPI.getAll(params);
      setProjects(response.data);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  }, [selectedCategory]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 lg:px-24" data-testid="work-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tighter leading-[0.9] mb-6" data-testid="work-title">
            My Work
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl">
            Explore my creative projects across different categories
          </p>
        </motion.div>

        <div className="mb-12 flex flex-wrap gap-4" data-testid="category-filters">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
              selectedCategory === 'all'
                ? 'bg-primary text-black'
                : 'bg-secondary hover:bg-white/5 text-white'
            }`}
            data-testid="category-filter-all"
          >
            All
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.name)}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                selectedCategory === category.name
                  ? 'bg-primary text-black'
                  : 'bg-secondary hover:bg-white/5 text-white'
              }`}
              data-testid={`category-filter-${category.slug}`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20" data-testid="loading-indicator">
            <p className="text-muted-foreground">Loading projects...</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20" data-testid="no-projects">
            <p className="text-muted-foreground">No projects found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Work;
